<template>
  <v-dialog
    :value="value"
    :fullscreen="fullscreen"
    max-width="560"
    :transition="fullscreen ? 'dialog-bottom-transition' : 'dialog-transition'"
    @input="v => !v && close()"
  >
    <v-card class="scanner" :class="{ 'scanner--full': fullscreen }">
      <div class="scanner__bar">
        <v-icon dark class="mr-2">mdi-qrcode-scan</v-icon>
        <span class="scanner__title">{{ title }}</span>
        <v-spacer />
        <v-btn icon dark large aria-label="Închide" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="scanner__stage">
        <video ref="video" class="scanner__video" :class="{ 'scanner__video--on': status === 'live' }" playsinline muted autoplay />

        <div v-show="status === 'live'" class="scanner__mask">
          <div ref="frame" class="scanner__frame">
            <span class="scanner__corner scanner__corner--tl" />
            <span class="scanner__corner scanner__corner--tr" />
            <span class="scanner__corner scanner__corner--bl" />
            <span class="scanner__corner scanner__corner--br" />
            <div class="scanner__laser" />
            <span class="scanner__aim" />
          </div>
        </div>
        <div v-if="status === 'live' && !notice" class="scanner__tip">
          Puneți în mijlocul chenarului codul pătrat (DataMatrix / QR) sau codul de bare de lângă „S/N”
        </div>

        <div v-if="status === 'starting' || status === 'photo'" class="scanner__center">
          <v-progress-circular indeterminate color="white" size="44" width="3" />
          <div class="mt-3">{{ status === "photo" ? "Se caută codurile în fotografie…" : "Se pornește camera…" }}</div>
        </div>

        <div v-if="status === 'error' || status === 'paused'" class="scanner__center scanner__message">
          <v-icon dark size="44">{{ status === "paused" ? "mdi-camera-outline" : errorIcon }}</v-icon>
          <div class="mt-3">{{ status === "paused" ? pausedText : cameraError }}</div>
          <v-btn v-if="(status === 'paused' && cameraAvailable()) || (status === 'error' && canRetry)" class="mt-4" outlined dark large @click="start">
            <v-icon left>mdi-camera</v-icon>
            {{ status === "paused" ? "Pornește camera" : "Încearcă din nou" }}
          </v-btn>
        </div>

        <transition name="fade">
          <div v-if="notice" class="scanner__notice" :class="{ 'scanner__notice--info': noticeInfo }" role="alert">
            <v-icon small color="white" class="mr-2">{{ noticeInfo ? "mdi-camera-flip-outline" : "mdi-alert-outline" }}</v-icon>
            <span>{{ notice }}</span>
          </div>
        </transition>

        <div v-if="status === 'choose'" class="scanner__choices">
          <div class="scanner__choices-title">
            <v-icon small color="#2d8659" class="mr-1">mdi-format-list-checks</v-icon>
            Am găsit mai multe coduri. Alegeți-l pe cel potrivit:
          </div>
          <div class="scanner__choices-list">
            <button
              v-for="c in choices"
              :key="c.text"
              type="button"
              class="scanner__choice"
              :disabled="!!c.reason"
              @click="accept(c.text)"
            >
              <span class="scanner__choice-text">{{ c.text }}</span>
              <span class="scanner__choice-meta">
                <span v-if="c.format" class="scanner__format">{{ c.format }}</span>
                <span v-if="c.reason" class="scanner__reason">{{ c.reason }}</span>
              </span>
            </button>
          </div>
          <v-btn text color="#2d8659" class="mt-1" @click="rescan">
            <v-icon left>mdi-refresh</v-icon>
            Scanează din nou
          </v-btn>
        </div>
      </div>

      <div class="scanner__controls">
        <v-btn
          v-if="status === 'live' && torchSupported"
          class="scanner__round"
          fab
          depressed
          :color="torchOn ? '#7fd1a3' : 'white'"
          :aria-label="torchOn ? 'Stinge lanterna' : 'Aprinde lanterna'"
          @click="toggleTorch"
        >
          <v-icon :color="torchOn ? '#0e2a21' : '#2d8659'">{{ torchOn ? "mdi-flashlight" : "mdi-flashlight-off" }}</v-icon>
        </v-btn>
        <v-btn
          v-if="status === 'live' && cameras.length > 1"
          class="scanner__round"
          fab
          depressed
          color="white"
          aria-label="Schimbă camera"
          @click="switchCamera"
        >
          <v-icon color="#2d8659">mdi-camera-flip-outline</v-icon>
        </v-btn>
        <v-btn class="scanner__photo" color="#2d8659" dark depressed x-large :disabled="status === 'photo'" @click="pickPhoto">
          <v-icon left>mdi-image-search-outline</v-icon>
          Din fotografie
        </v-btn>
      </div>

      <input ref="photo" type="file" accept="image/*" class="scanner__file" @change="onPhoto" @cancel="onPhotoCancel" />
    </v-card>
  </v-dialog>
</template>

<script>
// Reads a barcode / QR / DataMatrix with the camera (live) or from a photo. v-model = dialog open; emits scanned(text).
import { createReader, decodeImageFile, rejectReason, preloadDecoder, squareFirst } from "./decoder";

const CAMERA_KEY = "suport.scanner.camera";
const COLLECT_MS = 650; // after a first hit, keep looking a moment for other codes in view
const IGNORE_MS = 2500; // a refused code is not reported again right away

function storageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function storageSet(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (e) {
    // private mode: the choice is just not remembered
  }
}

export default {
  name: "BarcodeScanner",
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: "Scanează seria" },
    // what the scanned text may be (the default suits a seria)
    allowUrl: { type: Boolean, default: false },
    maxLength: { type: Number, default: 100 },
  },
  data() {
    return {
      status: "idle", // idle | starting | live | error | paused | photo | choose
      cameraError: "",
      errorIcon: "mdi-camera-off-outline",
      canRetry: false,
      pausedText: "Camera este oprită.",
      notice: "",
      noticeInfo: false,
      choices: [],
      cameras: [],
      torchSupported: false,
      torchOn: false,
    };
  },
  computed: {
    fullscreen() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  watch: {
    value(open) {
      if (open) this.onOpen();
      else this.shutdown();
    },
  },
  created() {
    this.session = 0;
    this.stream = null;
    this.ignored = new Map();
    this.onVisibility = () => {
      if (document.hidden) {
        if (this.status === "live" || this.status === "starting") {
          this.stopCamera();
          this.resumeWhenVisible = true;
        }
      } else if (this.resumeWhenVisible && this.value && this.status !== "photo" && this.status !== "choose") {
        this.resumeWhenVisible = false;
        this.start();
      }
    };
    document.addEventListener("visibilitychange", this.onVisibility);
  },
  mounted() {
    if (this.value) this.onOpen();
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopCamera();
    clearTimeout(this.noticeTimer);
  },
  methods: {
    onOpen() {
      this.choices = [];
      this.notice = "";
      this.ignored.clear();
      preloadDecoder(); // start downloading the decoder while the camera starts
      this.start();
    },
    close() {
      this.shutdown();
      this.$emit("input", false);
    },
    shutdown() {
      this.stopCamera();
      this.resumeWhenVisible = false;
      this.status = "idle";
      this.choices = [];
      this.notice = "";
    },
    accept(text) {
      if (navigator.vibrate) {
        try {
          navigator.vibrate(60);
        } catch (e) {
          // no vibration
        }
      }
      this.$emit("scanned", text);
      this.close();
    },

    // ---- camera
    cameraAvailable() {
      return window.isSecureContext !== false && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    },
    async waitForVideo() {
      for (let i = 0; i < 10 && !this.$refs.video; i++) {
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(r));
      }
      return this.$refs.video;
    },
    async start() {
      this.stopCamera();
      const session = ++this.session;
      this.choices = [];
      this.cameraError = "";
      if (!this.cameraAvailable()) {
        this.fail(
          "Camera live funcționează doar când aplicația este deschisă prin https:// (de exemplu https://suport.usarb.md). Apăsați „Din fotografie”: fotografiați codul și aplicația îl citește din imagine.",
          "mdi-lock-alert-outline",
          false
        );
        return;
      }
      this.status = "starting";
      try {
        // both at once; whichever fails, a camera already granted is switched off again
        const [opened, loaded] = await Promise.allSettled([this.openStream(), createReader()]);
        const stream = opened.status === "fulfilled" ? opened.value : null;
        const stale = session !== this.session || !this.value; // closed while starting
        if (stale || opened.status === "rejected" || loaded.status === "rejected") {
          if (stream) stream.getTracks().forEach(t => t.stop());
          if (stale) return;
          throw opened.status === "rejected" ? opened.reason : loaded.reason;
        }
        const reader = loaded.value;
        this.stream = stream;
        this.liveReader = reader;
        const video = await this.waitForVideo();
        if (!video || session !== this.session) {
          this.stopCamera();
          return;
        }
        video.muted = true;
        video.setAttribute("playsinline", "");
        video.srcObject = stream;
        try {
          await video.play();
        } catch (e) {
          // autoplay of a muted inline video is allowed; some browsers reject play() spuriously
        }
        if (session !== this.session) return;
        this.status = "live";
        this.setupTrack();
        this.listCameras();
        this.regionStep = 0;
        this.loop(session);
      } catch (error) {
        if (session !== this.session) return;
        this.stopCamera();
        this.failFromError(error);
      }
    },
    async openStream() {
      const size = { width: { ideal: 1920 }, height: { ideal: 1080 } };
      const back = { video: { facingMode: { ideal: "environment" }, ...size }, audio: false };
      const saved = storageGet(CAMERA_KEY);
      if (saved) {
        try {
          return await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: saved }, ...size }, audio: false });
        } catch (error) {
          if (!["OverconstrainedError", "NotFoundError", "NotReadableError"].includes(error.name)) throw error;
          storageSet(CAMERA_KEY, null); // that camera is gone: back to the default rear camera
        }
      }
      return navigator.mediaDevices.getUserMedia(back);
    },
    track() {
      return this.stream ? this.stream.getVideoTracks()[0] : null;
    },
    setupTrack() {
      const track = this.track();
      this.torchOn = false;
      this.torchSupported = false;
      if (!track || !track.getCapabilities) return;
      let caps = {};
      try {
        caps = track.getCapabilities() || {};
      } catch (e) {
        caps = {};
      }
      this.torchSupported = !!caps.torch;
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
        track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
      }
    },
    async listCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.cameras = devices.filter(d => d.kind === "videoinput" && d.deviceId);
      } catch (e) {
        this.cameras = [];
      }
    },
    async switchCamera() {
      if (this.cameras.length < 2) return;
      const track = this.track();
      const currentId = track && track.getSettings ? track.getSettings().deviceId : null;
      const at = this.cameras.findIndex(c => c.deviceId === currentId);
      const next = this.cameras[(at + 1) % this.cameras.length];
      storageSet(CAMERA_KEY, next.deviceId);
      await this.start();
      if (next.label) this.showNotice(next.label, 1800, true);
    },
    async toggleTorch() {
      const track = this.track();
      if (!track) return;
      const on = !this.torchOn;
      try {
        await track.applyConstraints({ advanced: [{ torch: on }] });
        this.torchOn = on;
      } catch (e) {
        this.torchSupported = false;
        this.showNotice("Lanterna nu poate fi aprinsă pe acest dispozitiv.");
      }
    },
    stopCamera() {
      this.session += 1;
      clearTimeout(this.loopTimer);
      clearTimeout(this.collectTimer);
      this.collecting = null;
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop());
        this.stream = null;
      }
      const video = this.$refs.video;
      if (video) {
        try {
          video.pause();
        } catch (e) {
          // nothing playing
        }
        video.srcObject = null;
      }
      this.torchOn = false;
      if (this.capture) {
        this.capture.width = 0;
        this.capture.height = 0;
      }
    },
    fail(message, icon, canRetry) {
      this.status = "error";
      this.cameraError = message;
      this.errorIcon = icon || "mdi-camera-off-outline";
      this.canRetry = canRetry;
    },
    failFromError(error) {
      const name = (error && error.name) || "";
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        this.fail(
          "Accesul la cameră a fost refuzat. Permiteți camera pentru acest site (iconița de lângă adresă sau Setări → Confidențialitate → Cameră) și încercați din nou, sau folosiți „Din fotografie”.",
          "mdi-camera-lock-outline",
          true
        );
      } else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
        this.fail("Nu a fost găsită nicio cameră pe acest dispozitiv. Folosiți „Din fotografie”.", "mdi-camera-off-outline", true);
      } else if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
        this.fail("Camera este folosită de altă aplicație. Închideți-o și încercați din nou, sau folosiți „Din fotografie”.", "mdi-camera-off-outline", true);
      } else if (name === "ChunkLoadError" || /chunk/i.test((error && error.message) || "")) {
        this.fail("Cititorul de coduri nu s-a putut încărca. Verificați internetul și încercați din nou.", "mdi-wifi-off", true);
      } else {
        this.fail("Camera nu a putut fi pornită. Încercați din nou sau folosiți „Din fotografie”.", "mdi-camera-off-outline", true);
      }
    },

    // ---- live decoding
    async loop(session) {
      if (session !== this.session) return;
      const video = this.$refs.video;
      if (video && video.readyState >= 2 && video.videoWidth && this.status === "live") {
        let results = [];
        try {
          results = await this.liveReader.decode(this.grab(video));
        } catch (e) {
          results = [];
        }
        if (session !== this.session) return;
        results.forEach(result => this.onLiveResult(result, session));
      }
      this.loopTimer = setTimeout(() => this.loop(session), this.collecting ? 50 : 100);
    },
    /**
     * The next region of the frame to decode. ZXing finds a DataMatrix / QR only near the middle of the region,
     * so the regions' centres move a little around the frame's centre (the browser's own detector looks everywhere).
     */
    grab(video) {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const frame = this.frameRect(video) || { x: vw * 0.1, y: vh * 0.25, w: vw * 0.8, h: vh * 0.5 };
      const step = this.regionStep++;
      const part = (fx, fy, fw, fh) => ({ x: frame.x + frame.w * fx, y: frame.y + frame.h * fy, w: frame.w * fw, h: frame.h * fh });
      const whole = { x: 0, y: 0, w: vw, h: vh };
      // while collecting (a code was seen): halves too, for the other codes of the label
      const parts = this.collecting
        ? [part(0, 0, 0.6, 1), part(0.4, 0, 0.6, 1), part(0, 0, 1, 0.6), part(0, 0.4, 1, 0.6), frame, whole]
        : [frame, part(0, 0, 0.8, 1), part(0.2, 0, 0.8, 1), frame, part(0, 0, 1, 0.7), part(0, 0.3, 1, 0.7), whole];
      const r = parts[step % parts.length];
      const maxSide = r === whole && !this.collecting ? 800 : 1024;
      const k = Math.min(1, maxSide / Math.max(r.w, r.h));
      if (!this.capture || !this.capture.width) {
        this.capture = document.createElement("canvas");
        this.captureCtx = this.capture.getContext("2d", { willReadFrequently: true });
      }
      const w = Math.max(1, Math.round(r.w * k));
      const h = Math.max(1, Math.round(r.h * k));
      if (this.capture.width !== w || this.capture.height !== h) {
        this.capture.width = w;
        this.capture.height = h;
        this.captureCtx = this.capture.getContext("2d", { willReadFrequently: true });
      }
      this.captureCtx.drawImage(video, r.x, r.y, r.w, r.h, 0, 0, w, h);
      return this.capture;
    },
    /** The on-screen frame in video pixels (the video is shown with object-fit: cover), with a margin */
    frameRect(video) {
      const frameEl = this.$refs.frame;
      if (!frameEl) return null;
      const box = video.getBoundingClientRect();
      const f = frameEl.getBoundingClientRect();
      if (!box.width || !f.width) return null;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const scale = Math.max(box.width / vw, box.height / vh);
      const offX = (box.width - vw * scale) / 2;
      const offY = (box.height - vh * scale) / 2;
      const marginX = f.width * 0.08;
      const marginY = f.height * 0.15;
      const x = Math.max(0, (f.left - box.left - offX - marginX) / scale);
      const y = Math.max(0, (f.top - box.top - offY - marginY) / scale);
      const w = Math.min(vw - x, (f.width + 2 * marginX) / scale);
      const h = Math.min(vh - y, (f.height + 2 * marginY) / scale);
      return w > 10 && h > 10 ? { x, y, w, h } : null;
    },
    onLiveResult(result, session) {
      const until = this.ignored.get(result.text);
      if (until && until > Date.now()) return;
      const reason = this.reasonFor(result);
      if (!this.collecting) {
        this.collecting = new Map();
        this.collectTimer = setTimeout(() => this.finishCollect(session), COLLECT_MS);
      }
      if (!this.collecting.has(result.text)) this.collecting.set(result.text, { ...result, reason });
    },
    finishCollect(session) {
      if (session !== this.session || !this.collecting) return;
      const all = [...this.collecting.values()];
      this.collecting = null;
      this.resolveFound(all, true);
    },
    reasonFor(result) {
      return rejectReason(result.text, { allowUrl: this.allowUrl, maxLength: this.maxLength, format: result.format });
    },
    /** One usable code → take it; several → let the user pick (square codes first); only refused ones → say why */
    resolveFound(all, live) {
      const usable = squareFirst(all.filter(r => !r.reason));
      if (usable.length === 1) {
        this.accept(usable[0].text);
        return;
      }
      if (usable.length > 1) {
        if (live) this.stopCamera();
        this.choices = [...usable, ...all.filter(r => r.reason)];
        this.status = "choose";
        return;
      }
      if (all.length) {
        all.forEach(r => this.ignored.set(r.text, Date.now() + IGNORE_MS));
        this.showNotice(all[0].reason, 5000);
      }
    },
    rescan() {
      this.choices = [];
      this.start();
    },
    showNotice(text, ms = 4000, info = false) {
      this.notice = text;
      this.noticeInfo = info;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => (this.notice = ""), ms);
    },

    // ---- from a photo
    pickPhoto() {
      // free the camera first: the phone's camera app cannot open it while we hold it
      const wasLive = this.status === "live" || this.status === "starting";
      this.stopCamera();
      if (wasLive || this.status === "choose") {
        this.status = "paused";
        this.pausedText = "Camera este oprită cât alegeți fotografia.";
      }
      this.resumeWhenVisible = false;
      this.$refs.photo.click();
    },
    onPhotoCancel() {
      if (this.status === "paused" && this.value && this.cameraAvailable()) this.start();
    },
    async onPhoto(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) {
        this.onPhotoCancel();
        return;
      }
      this.status = "photo";
      this.notice = "";
      this.choices = [];
      let found = [];
      try {
        found = await decodeImageFile(file, { isUsable: r => !this.reasonFor(r) });
      } catch (error) {
        this.afterPhoto();
        this.showNotice(error && error.name === "ImageError" ? error.message : "Fotografia nu a putut fi citită. Încercați din nou.", 6000);
        return;
      }
      if (!this.value) return;
      const all = found.map(r => ({ ...r, reason: this.reasonFor(r) }));
      if (!all.length) {
        this.afterPhoto();
        this.showNotice("Nu am găsit niciun cod în fotografie. Apropiați telefonul, evitați reflexiile și încercați din nou.", 6000);
        return;
      }
      this.afterPhoto();
      this.resolveFound(all, false);
    },
    afterPhoto() {
      this.status = "paused";
      this.pausedText = this.cameraAvailable()
        ? "Puteți porni camera sau alege altă fotografie."
        : "Camera live nu este disponibilă aici (doar prin https). Alegeți altă fotografie.";
    },
  },
};
</script>

<style scoped>
.scanner {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px !important;
  background: #0e2a21 !important;
}
.scanner--full {
  height: 100%;
  border-radius: 0 !important;
}
.scanner__bar {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 6px 6px 6px 16px;
  padding-top: calc(6px + env(safe-area-inset-top, 0px));
  background: #2d8659;
  color: white;
}
.scanner__title {
  font-size: 18px;
  font-weight: 600;
}
.scanner__stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 360px;
  background: #000;
  overflow: hidden;
}
.scanner__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
}
.scanner__video--on {
  opacity: 1;
}
.scanner__mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}
.scanner__frame {
  position: absolute;
  left: 8%;
  right: 8%;
  top: 50%;
  height: 46%;
  max-height: 280px;
  transform: translateY(-55%);
  border-radius: 14px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
}
.scanner__corner {
  position: absolute;
  width: 28px;
  height: 28px;
  border: 0 solid #7fd1a3;
}
.scanner__corner--tl {
  top: -2px;
  left: -2px;
  border-top-width: 4px;
  border-left-width: 4px;
  border-top-left-radius: 14px;
}
.scanner__corner--tr {
  top: -2px;
  right: -2px;
  border-top-width: 4px;
  border-right-width: 4px;
  border-top-right-radius: 14px;
}
.scanner__corner--bl {
  bottom: -2px;
  left: -2px;
  border-bottom-width: 4px;
  border-left-width: 4px;
  border-bottom-left-radius: 14px;
}
.scanner__corner--br {
  bottom: -2px;
  right: -2px;
  border-bottom-width: 4px;
  border-right-width: 4px;
  border-bottom-right-radius: 14px;
}
.scanner__laser {
  position: absolute;
  left: 6%;
  right: 6%;
  height: 2px;
  top: 10%;
  background: #7fd1a3;
  box-shadow: 0 0 8px #7fd1a3;
  animation: scanner-laser 1.8s ease-in-out infinite alternate;
}
.scanner__aim {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 44px;
  height: 44px;
  margin: -22px 0 0 -22px;
  border: 2px solid rgba(127, 209, 163, 0.9);
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
}
.scanner__aim::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  margin: -3px 0 0 -3px;
  border-radius: 50%;
  background: #7fd1a3;
}
@keyframes scanner-laser {
  from {
    top: 10%;
  }
  to {
    top: 88%;
  }
}
.scanner__tip {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 14px;
  text-align: center;
  color: white;
  font-size: 14px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}
.scanner__center {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: white;
  text-align: center;
  font-size: 15px;
  line-height: 1.45;
}
.scanner__message {
  background: #0e2a21;
}
.scanner__notice {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(179, 54, 47, 0.95);
  color: white;
  font-size: 14px;
  line-height: 1.4;
  z-index: 2;
}
.scanner__notice--info {
  background: rgba(14, 42, 33, 0.92);
}
.scanner__choices {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 100%;
  overflow-y: auto;
  padding: 16px;
  background: white;
  border-radius: 12px 12px 0 0;
  z-index: 3;
}
.scanner__choices-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d8659;
  margin-bottom: 10px;
}
.scanner__choices-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.scanner__choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-height: 52px;
  padding: 10px 12px;
  text-align: left;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafcfb;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.scanner__choice:not(:disabled):hover,
.scanner__choice:not(:disabled):focus-visible {
  border-color: #2d8659;
  box-shadow: 0 2px 8px rgba(45, 134, 89, 0.15);
  outline: none;
}
.scanner__choice:disabled {
  opacity: 0.75;
  background: #f5f5f5;
}
.scanner__choice-text {
  font-family: "Roboto Mono", Consolas, monospace;
  font-size: 15px;
  word-break: break-all;
  color: #212121;
}
.scanner__choice-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
}
.scanner__format {
  padding: 1px 8px;
  border-radius: 10px;
  background: #ecf9f2;
  color: #2d8659;
  font-weight: 600;
}
.scanner__reason {
  color: #b3362f;
}
.scanner__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  background: #0e2a21;
}
.scanner__photo {
  flex: 1 1 auto;
  text-transform: none;
  letter-spacing: normal;
  font-size: 16px;
  border-radius: 10px;
}
.scanner__file {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
